/**
 * Template Copy Utilities
 *
 * Templates are campaigns with `isTemplate: true`. These helpers deep-copy a
 * campaign/template tree (including nested campaigns) either:
 * - template → new campaign (spawn), or
 * - campaign → new template (save as template / duplicate template)
 *
 * Campaign fields are copied by exclusion list so new features are preserved
 * by default. Task IDs and cross-references are always regenerated/remapped.
 */
import { remapConditionTaskIds } from '../components/SprintDetail/shared/ConditionsDropdown/utils/conditionHelpers';
import { collectMissionLinkedDescendants } from './nestedCampaignInvariant';

/** Identity / runtime fields — never copy; caller sets what is needed. */
const STRIP_FROM_CAMPAIGN_COPY = new Set([
  'id',
  'createdAt',
  'updatedAt',
  'createdBy',
  'ownerId',
  'ownerName',
  'deletedAt',
  'expiresAt',
  '_originalParentSprintId',
  '_originalParentTaskId',
  '_trashedViaAncestorId',
  'parentSprintId',
  'parentTaskId',
  'isTemplate',
  'templateId',
  'status',
  'progress',
  // Handled / remapped separately:
  'tasks',
  'edges',
  'connections',
  'childOrder',
]);

function generateId(prefix = 'task') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function deepCloneValue(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object') return value;
  return JSON.parse(JSON.stringify(value));
}

/**
 * Copy campaign-level settings by exclusion list (customFields, chains, views, etc.).
 */
function copyCampaignShell(source) {
  const shell = {};
  for (const [key, value] of Object.entries(source || {})) {
    if (STRIP_FROM_CAMPAIGN_COPY.has(key)) continue;
    if (value === undefined) continue;
    shell[key] = deepCloneValue(value);
  }
  return shell;
}

function remapChildOrder(childOrder, nestedIdMap) {
  if (!Array.isArray(childOrder) || childOrder.length === 0) return undefined;
  const remapped = childOrder
    .map(id => nestedIdMap[id] || id)
    .filter(Boolean);
  return remapped.length > 0 ? remapped : undefined;
}

function remapEdges(edges, idMap) {
  if (!edges || edges.length === 0) return [];

  return edges.map(edge => ({
    ...edge,
    id: generateId('edge'),
    source: idMap[edge.source] || edge.source,
    target: idMap[edge.target] || edge.target,
  }));
}

function getStatusCategory(statusId, statuses = []) {
  if (!statusId) return 'to_do';

  const status = statuses.find(s => s.id === statusId);
  if (status?.category) return status.category;

  if (statusId === 'completed' || statusId === 'done') return 'completed';
  if (statusId === 'in_progress' || statusId === 'in-progress') return 'in_progress';
  return 'to_do';
}

/**
 * Process date fields when copying a task from a template to a campaign.
 *
 * - dueDate: stripped (set manually in campaigns)
 * - estimatedDurationMinutes: kept
 * - createdAt / updatedAt: campaign creation time
 * - startedAt / completedAt: set when status category matches
 */
function processTaskDateFields(task, statuses = []) {
  const now = new Date().toISOString();
  const statusCategory = getStatusCategory(task.status, statuses);
  const { dueDate, ...rest } = task;

  return {
    ...rest,
    createdAt: now,
    updatedAt: now,
    startedAt: statusCategory === 'in_progress' || statusCategory === 'completed' ? now : null,
    completedAt: statusCategory === 'completed' ? now : null,
  };
}

function remapTaskReferences(task, idMap) {
  if (task.milestoneAnchor?.hostTaskId && idMap[task.milestoneAnchor.hostTaskId]) {
    task.milestoneAnchor = {
      ...task.milestoneAnchor,
      hostTaskId: idMap[task.milestoneAnchor.hostTaskId],
    };
  }

  if (Array.isArray(task.dependsOn) && task.dependsOn.length > 0) {
    task.dependsOn = task.dependsOn.map(dep => {
      if (typeof dep === 'string') {
        return idMap[dep] || dep;
      }
      if (dep && dep.taskId) {
        return { ...dep, taskId: idMap[dep.taskId] || dep.taskId };
      }
      return dep;
    });
  }

  if (task.unlockConditions) {
    task.unlockConditions = remapConditionTaskIds(task.unlockConditions, idMap);
  }
}

/**
 * Deep-copy tasks with new IDs and remapped references.
 *
 * @param {Array} tasks
 * @param {Object} options
 * @param {Array} options.statuses - for date field processing when resetting working state
 * @param {boolean} options.resetWorkingState - template→campaign: strip due dates, reset docTicks
 */
function copyTasks(tasks, options = {}) {
  const { statuses = [], resetWorkingState = false } = options;

  if (!tasks || tasks.length === 0) {
    return { tasks: [], idMap: {} };
  }

  const idMap = {};
  const copiedTasks = tasks.map(task => {
    const newId = generateId('task');
    idMap[task.id] = newId;

    let base = {
      ...task,
      id: newId,
      dependsOn: Array.isArray(task.dependsOn) ? [...task.dependsOn] : [],
      subtasks: Array.isArray(task.subtasks) ? [...task.subtasks] : [],
    };

    if (resetWorkingState) {
      base = processTaskDateFields(base, statuses);
      base.attachedDocs = Array.isArray(base.attachedDocs)
        ? base.attachedDocs.map(entry => ({ ...entry }))
        : [];
      // Checkbox progress, upload-field files, and mission variable values
      base.docTicks = {};
      base.docFiles = {};
      base.variableValues = [];
    }

    return base;
  });

  copiedTasks.forEach(task => remapTaskReferences(task, idMap));

  return { tasks: copiedTasks, idMap };
}

function findAllNestedCampaigns(rootId, allSprints) {
  // Mission cards only — parentSprintId ghosts must not be copied into new trees.
  return collectMissionLinkedDescendants(rootId, allSprints);
}

function buildNestedIdMap(nestedCampaigns) {
  const nestedIdMap = {};
  nestedCampaigns.forEach(nested => {
    nestedIdMap[nested.id] = generateId('sprint');
  });
  return nestedIdMap;
}

function remapNestedSprintIdsOnTasks(tasks, nestedIdMap) {
  tasks.forEach(task => {
    if (task.nestedSprintId && nestedIdMap[task.nestedSprintId]) {
      task.nestedSprintId = nestedIdMap[task.nestedSprintId];
    }
  });
}

function copyNestedCampaigns(nestedCampaigns, nestedIdMap, { resetWorkingState }) {
  return nestedCampaigns.map(nested => {
    const newNestedId = nestedIdMap[nested.id];
    const { tasks: nestedTasks, idMap: nestedTaskIdMap } = copyTasks(nested.tasks, {
      statuses: nested.statuses,
      resetWorkingState,
    });

    remapNestedSprintIdsOnTasks(nestedTasks, nestedIdMap);

    return {
      ...copyCampaignShell(nested),
      id: newNestedId,
      name: nested.name,
      icon: nested.icon,
      tasks: nestedTasks,
      edges: remapEdges(nested.edges, nestedTaskIdMap),
      connections: remapEdges(nested.connections, nestedTaskIdMap),
      childOrder: remapChildOrder(nested.childOrder, nestedIdMap),
      parentSprintId: null,
      parentTaskId: null,
      isTemplate: false,
      status: 'active',
    };
  });
}

function resolveNestedParentLinks(copiedNestedCampaigns, nestedCampaigns, nestedIdMap, rootId, rootTasks) {
  copiedNestedCampaigns.forEach((copied, index) => {
    const original = nestedCampaigns[index];
    const newNestedId = copied.id;

    if (original.parentSprintId === rootId) {
      const parentTask = rootTasks.find(t => t.nestedSprintId === newNestedId);
      copied.parentTaskId = parentTask?.id || null;
    } else {
      copied.parentSprintId = nestedIdMap[original.parentSprintId] || null;
      const parentNested = copiedNestedCampaigns.find(n => n.id === copied.parentSprintId);
      if (parentNested) {
        const parentTask = parentNested.tasks.find(t => t.nestedSprintId === newNestedId);
        copied.parentTaskId = parentTask?.id || null;
      }
    }
  });
}

/**
 * Deep-copy a template and all its nested campaigns into a new campaign tree.
 */
export function deepCopyTemplate(template, allSprints = [], options = {}) {
  const { newName, newIcon, parentSprintId } = options;

  const nestedCampaigns = findAllNestedCampaigns(template.id, allSprints);
  const nestedIdMap = buildNestedIdMap(nestedCampaigns);

  const { tasks: copiedTasks, idMap: taskIdMap } = copyTasks(template.tasks, {
    statuses: template.statuses,
    resetWorkingState: true,
  });
  remapNestedSprintIdsOnTasks(copiedTasks, nestedIdMap);

  const rootCampaign = {
    ...copyCampaignShell(template),
    name: newName || template.name,
    icon: newIcon !== undefined ? newIcon : template.icon,
    tasks: copiedTasks,
    edges: remapEdges(template.edges, taskIdMap),
    connections: remapEdges(template.connections, taskIdMap),
    childOrder: remapChildOrder(template.childOrder, nestedIdMap),
    parentSprintId: parentSprintId || null,
    templateId: template.id,
    isTemplate: false,
  };

  const copiedNestedCampaigns = copyNestedCampaigns(nestedCampaigns, nestedIdMap, {
    resetWorkingState: true,
  });

  resolveNestedParentLinks(
    copiedNestedCampaigns,
    nestedCampaigns,
    nestedIdMap,
    template.id,
    copiedTasks,
  );

  return {
    rootCampaign,
    nestedCampaigns: copiedNestedCampaigns,
    nestedIdMap,
  };
}

/**
 * Finalize nested campaigns after root campaign/template is created.
 * Sets parentSprintId on direct children of the root.
 */
export function finalizeNestedCampaigns(nestedCampaigns, rootCampaignId) {
  return nestedCampaigns.map(nested => ({
    ...nested,
    parentSprintId: nested.parentSprintId || rootCampaignId,
  }));
}

/**
 * Deep-copy a campaign (or template) to create a new template tree.
 * Preserves task working state; regenerates IDs and remaps references.
 */
export function createTemplateFromCampaign(campaign, allSprints = [], options = {}) {
  const { newName } = options;

  const nestedCampaigns = findAllNestedCampaigns(campaign.id, allSprints);
  const nestedIdMap = buildNestedIdMap(nestedCampaigns);

  const { tasks: copiedTasks, idMap: taskIdMap } = copyTasks(campaign.tasks, {
    resetWorkingState: false,
  });
  remapNestedSprintIdsOnTasks(copiedTasks, nestedIdMap);

  const rootTemplate = {
    ...copyCampaignShell(campaign),
    name: newName || `${campaign.name} (Template)`,
    icon: campaign.icon,
    tasks: copiedTasks,
    edges: remapEdges(campaign.edges, taskIdMap),
    connections: remapEdges(campaign.connections, taskIdMap),
    childOrder: remapChildOrder(campaign.childOrder, nestedIdMap),
    isTemplate: true,
    parentSprintId: null,
    parentTaskId: null,
  };

  const copiedNestedCampaigns = copyNestedCampaigns(nestedCampaigns, nestedIdMap, {
    resetWorkingState: false,
  });

  resolveNestedParentLinks(
    copiedNestedCampaigns,
    nestedCampaigns,
    nestedIdMap,
    campaign.id,
    copiedTasks,
  );

  return {
    rootTemplate,
    nestedCampaigns: copiedNestedCampaigns,
    nestedIdMap,
  };
}

/**
 * Load a template/campaign and all nested campaigns with full task data.
 * The list API returns metadata only, so this must run before copy operations.
 */
export async function loadTemplateTreeForCopy(templateId, allSprints, loadFullSprint) {
  const sprints = [...allSprints];

  const upsertSprint = (sprint) => {
    const idx = sprints.findIndex(s => s.id === sprint.id);
    if (idx >= 0) sprints[idx] = sprint;
    else sprints.push(sprint);
  };

  let root = sprints.find(s => s.id === templateId);
  if (!root || !Array.isArray(root.tasks)) {
    const loaded = await loadFullSprint(templateId);
    if (loaded) {
      upsertSprint(loaded);
      root = loaded;
    }
  }

  if (!root) return { root: null, sprints };

  const loadedIds = new Set();
  const queue = [templateId];
  while (queue.length) {
    const id = queue.shift();
    if (!id || loadedIds.has(id)) continue;
    loadedIds.add(id);

    let sprint = sprints.find((s) => s.id === id);
    if (!sprint || !Array.isArray(sprint.tasks)) {
      const loaded = await loadFullSprint(id);
      if (loaded) {
        upsertSprint(loaded);
        sprint = loaded;
      }
    }
    for (const task of sprint?.tasks || []) {
      if (task?.nestedSprintId) queue.push(task.nestedSprintId);
    }
  }

  root = sprints.find(s => s.id === templateId) || root;
  return { root, sprints };
}

/**
 * Persist a newly built template tree (root + nested) via SprintAPI.create.
 * Shared by "save as template" and "duplicate template".
 */
export async function persistTemplateTree({ rootTemplate, nestedCampaigns, SprintAPI }) {
  const createdTemplate = await SprintAPI.create({
    ...rootTemplate,
    status: 'active',
  });

  const finalizedNested = finalizeNestedCampaigns(nestedCampaigns, createdTemplate.id);
  const createdNested = [];
  for (const nested of finalizedNested) {
    const created = await SprintAPI.create(nested);
    createdNested.push(created);
  }

  return { createdTemplate, createdNested };
}
